import { useState } from "react";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import HeroSection from "./HeroSection";          // Same folder
import heroImage from "../assets/hero-industrial.jpg"; // Adjust if needed
import { openWhatsAppWithMessage } from "../../utils/whatsapp";
import { trackEnquirySubmit } from "../../utils/analytics";

const Enquiry = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // New handler specifically for phone input to accept only numbers (up to 10 digits)
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Logging the enquiry to Firebase is best-effort — it must never block or
    // skip handing the enquiry off to WhatsApp, which is the real delivery
    // channel here.
    try {
      const response = await fetch("https://vasavi-fbdf5-default-rtdb.firebaseio.com/ServiceDetails.json");
      const currentData = await response.json();
      const currentCount = currentData ? Object.keys(currentData).length : 0;
      const nextId = String(currentCount + 1).padStart(3, "0");

      const dataToStore = {
        fullName: formData.name,
        phoneNumber: formData.phone,
        email: formData.email,
        service: formData.service,
        message: formData.message,
        timestamp: new Date().toISOString()
      };

      await fetch(`https://vasavi-fbdf5-default-rtdb.firebaseio.com/ServiceDetails/${nextId}.json`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataToStore),
      });
    } catch (error) {
      console.error("Error logging enquiry to Firebase:", error);
    }

    setSubmitted(true);
    trackEnquirySubmit(formData.service);
    openWhatsAppWithMessage("New Enquiry — TR Structures Website", {
      "Name": formData.name,
      "Phone": formData.phone,
      "Email": formData.email,
      "Service Required": formData.service,
      "Message": formData.message,
    });
    setIsSubmitting(false);
  };

  const services = [
    "Industrial Shed Construction",
    "Prefabricated Building",
    "Fabrication Services",
    "Industrial Steel Structures",
    "Shed Fabrication & Installation",
    "Site Visit Request",
    "Quotation / Estimate",
    "Other",
  ];

  const steps = [
    {
      title: "Understanding Your Requirement",
      description: "Our experts will connect to understand your needs, budget, and project expectations.",
    },
    {
      title: "Site Assessment",
      description: "For industrial sheds and structures, we conduct site visits to measure space and requirements.",
    },
    {
      title: "Planning & Estimate",
      description: "We prepare a clear, transparent quotation with material specifications and timelines.",
    },
    {
      title: "Design & Execution",
      description: "Upon approval, our team creates structural designs and initiates fabrication.",
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Get Industrial Shed & Fabrication Quotes | Enquiry – TR Structures"
        description="Submit your enquiry for industrial sheds, PEB structures, fabrication services & prefabricated buildings in Hyderabad. Get accurate pricing, expert guidance & quick responses from TR Structures."
        keywords="industrial shed quotation Hyderabad, fabrication services enquiry, PEB structure quote, prefab building cost Hyderabad, warehouse shed estimate, industrial construction pricing"
      />



      <main>
        <HeroSection
          title="Send Us Your Enquiry"
          subtitle="Get Quick Responses & Accurate Pricing"
          description="Whether you need an industrial shed, fabrication service, or prefabricated building, our team is here to help with professional assistance."
          backgroundImage={heroImage}
        />

        {/* Enquiry Form Section */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Form */}
              <div className="animate-slide-in-left">
                <h2 className="font-montserrat text-2xl sm:text-4xl font-bold text-foreground mb-6">
                  Submit Your <span className="text-secondary">Enquiry</span>
                </h2>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 sm:p-10 text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <svg className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-montserrat text-1xl sm:text-2xl font-bold text-green-800 mb-3">
                      Enquiry Submitted Successfully!
                    </h3>
                    <p className="text-green-700 text-base sm:text-lg">
                      Thank you! Our team will contact you within 24 hours. We've also opened
                      WhatsApp with your enquiry pre-filled — hit send there for the fastest response.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-medium text-foreground mb-2 text-1xl sm:text-base">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-1xl sm:text-base"
                          placeholder="Your Name"
                        />
                      </div>
                      <div>
                        <label className="block font-medium text-foreground mb-2 text-1xl sm:text-base">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}  // Updated to use phone-specific handler
                          required
                          maxLength={10}  // Optional: Enforce max length in input
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-1xl sm:text-base"
                          placeholder="XXXXXXXXXX"  // Updated placeholder to reflect 10 digits only
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-foreground mb-2 text-1xl sm:text-base">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-1xl sm:text-base"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label className="block font-medium text-foreground mb-2 text-1xl sm:text-base">Service Required *</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-background text-foreground text-1xl sm:text-base"
                      >
                        <option value="">Select a Service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-medium text-foreground mb-2 text-1xl sm:text-base">Your Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none text-1xl sm:text-base"
                        placeholder="Describe your project requirements, size, location, budget, etc..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-secondary w-full py-4 text-base sm:text-lg font-medium hover:bg-secondary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                    </button>
                  </form>
                )}
              </div>

              {/* Process Steps */}
              <div className="animate-slide-in-right">
                <h2 className="font-montserrat text-2xl sm:text-3xl font-bold text-foreground mb-6">
                  What Happens <span className="text-primary">Next?</span>
                </h2>

                <div className="space-y-8">
                  {steps.map((step, index) => (
                    <div key={index} className="flex gap-4 sm:gap-5">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-montserrat font-bold text-lg sm:text-xl shadow-lg">
                          {index + 1}
                        </div>
                      </div>
                      <div className="pt-1 sm:pt-2">
                        <h3 className="font-montserrat font-bold text-foreground text-2xl sm:text-lg mb-1">
                          {step.title}
                        </h3>
                        <p className="text-muted-foreground text-1xl sm:text-base">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Why Contact */}
                <div className="mt-10 p-4 sm:p-6 bg-muted rounded-xl border border-border">
                  <h3 className="font-montserrat font-bold text-foreground mb-5 text-2xl sm:text-lg">
                    Why Choose TR Structures?
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    {[
                      "Professional engineering support",
                      "Affordable & transparent pricing",
                      "Quick response & timely updates",
                      "Zero hidden costs",
                      "On-time project delivery",
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-1xl sm:text-base">
                        <svg className="w-5 h-5 text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

    </Layout>
  );
};

export default Enquiry;